import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, addDoc, getDocs, setDoc, getDoc, doc, query, where, Timestamp, writeBatch } from "firebase/firestore";
import MyPayslips from "./MyPayslips";

// Excelの読み書きライブラリは大きいので、実際に使うときだけ読み込む。
// 初回表示までの待ち時間を減らすため。
const loadXLSX = () => import("xlsx");

// デフォルトテンプレート
const DEFAULT_FIELDS = [
  { name: "基本給",   type: "支給" },
  { name: "残業手当", type: "支給" },
  { name: "通勤手当", type: "支給" },
  { name: "健康保険", type: "控除" },
  { name: "厚生年金", type: "控除" },
  { name: "雇用保険", type: "控除" },
  { name: "所得税",   type: "控除" },
  { name: "住民税",   type: "控除" },
];

// ========== テンプレート管理 ==========
function TemplateTab({ companyId }) {
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { loadTemplate(); }, []);

  const loadTemplate = async () => {
    if (!companyId) { setFields(DEFAULT_FIELDS); return; }
    const snap = await getDoc(doc(db, "payslipTemplates", companyId));
    if (snap.exists()) setFields(snap.data().fields || []);
    else setFields(DEFAULT_FIELDS);
  };

  const addField = () => setFields([...fields, { name: "", type: "支給" }]);
  const removeField = (i) => setFields(fields.filter((_, idx) => idx !== i));
  const updateField = (i, key, val) => {
    const next = [...fields];
    next[i] = { ...next[i], [key]: val };
    setFields(next);
  };

  const save = async () => {
    setSaving(true);
    await setDoc(doc(db, "payslipTemplates", companyId), { fields, updatedAt: Timestamp.now() });
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  // Excelテンプレートダウンロード
  const downloadExcelTemplate = async () => {
    const XLSX = await loadXLSX();
    const headers = ["対象メール", "対象月(YYYY-MM)", ...fields.map(f => f.name)];
    const ws = XLSX.utils.aoa_to_sheet([
      headers,
      ["example@email.com", "2026-08", ...fields.map(() => "")],
    ]);
    // 列幅
    ws["!cols"] = headers.map((h, i) => ({ wch: i < 2 ? 22 : Math.max(h.length * 2, 12) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "給与明細");
    XLSX.writeFile(wb, "給与明細テンプレート.xlsx");
  };

  const payFields  = fields.filter(f => f.type === "支給");
  const dedFields  = fields.filter(f => f.type === "控除");

  return (
    <div style={st.form}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h3 style={{ color: "#2c3e50", margin: 0 }}>給与明細テンプレート</h3>
        <button style={st.dlBtn} onClick={downloadExcelTemplate} type="button">⬇ Excelテンプレート</button>
      </div>

      <p style={{ fontSize: "13px", color: "#7f8c8d", marginBottom: "20px" }}>
        支給・控除の項目を定義します。テンプレートを保存後、Excelテンプレートをダウンロードして使えます。
      </p>

      <table style={st.tbl}>
        <thead>
          <tr>
            <th style={st.th}>項目名</th>
            <th style={st.th}>種別</th>
            <th style={{ ...st.th, width: "60px" }}></th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f, i) => (
            <tr key={i}>
              <td style={st.td}>
                <input style={st.tblInput} value={f.name}
                  onChange={e => updateField(i, "name", e.target.value)}
                  placeholder="例：基本給" />
              </td>
              <td style={st.td}>
                <select style={st.tblSelect} value={f.type}
                  onChange={e => updateField(i, "type", e.target.value)}>
                  <option value="支給">支給</option>
                  <option value="控除">控除</option>
                  <option value="その他">その他</option>
                </select>
              </td>
              <td style={st.td}>
                <button style={st.delBtn} onClick={() => removeField(i)} type="button">✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button style={st.addRowBtn} onClick={addField} type="button">＋ 項目を追加</button>

      <div style={{ marginTop: "24px", display: "flex", gap: "12px", alignItems: "center" }}>
        <button style={st.submitBtn} onClick={save} disabled={saving} type="button">
          {saving ? "保存中..." : "テンプレートを保存"}
        </button>
        {saved && <span style={{ color: "#27ae60", fontSize: "14px" }}>✅ 保存しました</span>}
      </div>

      {fields.length > 0 && (
        <div style={{ marginTop: "28px", padding: "16px", backgroundColor: "#f8fafc", borderRadius: "10px", fontSize: "13px" }}>
          <div style={{ fontWeight: "bold", color: "#2c3e50", marginBottom: "10px" }}>プレビュー</div>
          {payFields.length > 0 && <>
            <div style={st.previewSection}>支給</div>
            {payFields.map((f, i) => <div key={i} style={st.previewRow}><span>{f.name}</span><span style={{ color: "#999" }}>¥ 0</span></div>)}
          </>}
          {dedFields.length > 0 && <>
            <div style={st.previewSection}>控除</div>
            {dedFields.map((f, i) => <div key={i} style={st.previewRow}><span>{f.name}</span><span style={{ color: "#e74c3c" }}>¥ 0</span></div>)}
          </>}
          <div style={{ ...st.previewRow, borderTop: "2px solid #2c3e50", marginTop: "8px", paddingTop: "8px", fontWeight: "bold" }}>
            <span>差引支給額</span><span style={{ color: "#27ae60" }}>¥ 0</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ========== 手入力発行 ==========
function IssueTab({ user, companyId, onIssued }) {
  const [template, setTemplate] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [targetEmail, setTargetEmail] = useState("");
  const [yearMonth, setYearMonth] = useState("");
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadTemplate();
    loadEmployees();
  }, []);

  const loadTemplate = async () => {
    if (!companyId) { setTemplate({ fields: DEFAULT_FIELDS }); return; }
    const snap = await getDoc(doc(db, "payslipTemplates", companyId));
    if (snap.exists()) setTemplate(snap.data());
    else setTemplate({ fields: DEFAULT_FIELDS });
  };

  const loadEmployees = async () => {
    if (!companyId) return;
    const q = query(collection(db, "users"), where("companyId", "==", companyId), where("status", "==", "approved"));
    const snap = await getDocs(q);
    setEmployees(snap.docs.map(d => d.data()));
  };

  const calcNet = () => {
    if (!template) return 0;
    return template.fields.reduce((acc, f) => {
      const v = Number(values[f.name]) || 0;
      if (f.type === "支給") return acc + v;
      if (f.type === "控除") return acc - v;
      return acc;
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!template) { alert("テンプレートを先に作成してください"); return; }
    setSubmitting(true);
    try {
      await addDoc(collection(db, "payslips"), {
        targetEmail,
        companyId: companyId || "",
        yearMonth,
        values,
        netPay: calcNet(),
        issuedBy: user.email,
        createdAt: Timestamp.now(),
      });
      setValues({}); setTargetEmail(""); setYearMonth("");
      onIssued();
    } catch (err) {
      console.error(err);
      alert("発行に失敗しました。もう一度お試しください。");
    } finally {
      setSubmitting(false);
    }
  };

  if (!template) return <div style={st.form}><p style={{color:"#999"}}>読み込み中...</p></div>;

  const payFields   = template.fields.filter(f => f.type === "支給");
  const dedFields   = template.fields.filter(f => f.type === "控除");
  const otherFields = template.fields.filter(f => f.type === "その他");

  return (
    <form onSubmit={handleSubmit} style={st.form}>
      <h3 style={{ color: "#2c3e50", marginBottom: "20px" }}>給与明細を発行</h3>

      <label style={st.label}>対象者</label>
      {employees.length > 0
        ? <select style={st.input} value={targetEmail} onChange={e => setTargetEmail(e.target.value)} required>
            <option value="">選択してください</option>
            {employees.map(emp => <option key={emp.email} value={emp.email}>{emp.name}（{emp.email}）</option>)}
          </select>
        : <input style={st.input} type="email" placeholder="メールアドレスを直接入力" value={targetEmail} onChange={e => setTargetEmail(e.target.value)} required />
      }

      <label style={st.label}>対象月</label>
      <input style={st.input} type="month" value={yearMonth} onChange={e => setYearMonth(e.target.value)} required />

      {payFields.length > 0 && (
        <div style={st.fieldGroup}>
          <div style={st.groupLabel}>支給</div>
          {payFields.map(f => (
            <div key={f.name} style={st.fieldRow}>
              <label style={st.fieldName}>{f.name}</label>
              <input style={st.amtInput} type="number" min="0" placeholder="0"
                value={values[f.name] || ""}
                onChange={e => setValues({ ...values, [f.name]: e.target.value })} />
              <span style={st.yen}>円</span>
            </div>
          ))}
        </div>
      )}
      {dedFields.length > 0 && (
        <div style={st.fieldGroup}>
          <div style={st.groupLabel}>控除</div>
          {dedFields.map(f => (
            <div key={f.name} style={st.fieldRow}>
              <label style={st.fieldName}>{f.name}</label>
              <input style={st.amtInput} type="number" min="0" placeholder="0"
                value={values[f.name] || ""}
                onChange={e => setValues({ ...values, [f.name]: e.target.value })} />
              <span style={st.yen}>円</span>
            </div>
          ))}
        </div>
      )}
      {otherFields.length > 0 && (
        <div style={st.fieldGroup}>
          <div style={st.groupLabel}>その他</div>
          {otherFields.map(f => (
            <div key={f.name} style={st.fieldRow}>
              <label style={st.fieldName}>{f.name}</label>
              <input style={st.amtInput} type="number" placeholder="0"
                value={values[f.name] || ""}
                onChange={e => setValues({ ...values, [f.name]: e.target.value })} />
              <span style={st.yen}>円</span>
            </div>
          ))}
        </div>
      )}

      <div style={st.netRow}>
        <span>差引支給額</span>
        <span style={{ color: "#27ae60", fontWeight: "bold", fontSize: "20px" }}>
          ¥ {calcNet().toLocaleString("ja-JP")}
        </span>
      </div>

      <button style={st.submitBtn} type="submit" disabled={submitting}>
        {submitting ? "発行中..." : "発行する"}
      </button>
    </form>
  );
}

// ========== Excelアップロード ==========
function UploadTab({ user, companyId, onIssued }) {
  const [template, setTemplate] = useState(null);
  const [preview, setPreview] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { loadTemplate(); }, []);

  const loadTemplate = async () => {
    if (!companyId) { setTemplate({ fields: DEFAULT_FIELDS }); return; }
    const snap = await getDoc(doc(db, "payslipTemplates", companyId));
    if (snap.exists()) setTemplate(snap.data());
    else setTemplate({ fields: DEFAULT_FIELDS });
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const XLSX = await loadXLSX();
      const wb = XLSX.read(ev.target.result, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
      if (rows.length < 2) { alert("データが空です"); return; }
      const headers = rows[0];
      const data = rows.slice(1).filter(r => r.some(c => c !== undefined && c !== ""));
      setPreview(data.map(row => {
        const obj = {};
        headers.forEach((h, i) => { obj[h] = row[i] ?? ""; });
        return obj;
      }));
    };
    reader.readAsArrayBuffer(file);
  };

  const handleUpload = async () => {
    if (!template || preview.length === 0) return;
    setUploading(true);
    try {
      // writeBatchで一括書き込み（Firestoreは500件/バッチ上限）
      const batch = writeBatch(db);
      let count = 0;
      for (const row of preview) {
        const email = row["対象メール"];
        const month = String(row["対象月(YYYY-MM)"]);
        if (!email || !month) continue;
        const values = {};
        template.fields.forEach(f => { values[f.name] = Number(row[f.name]) || 0; });
        const netPay = template.fields.reduce((acc, f) => {
          const v = Number(values[f.name]) || 0;
          if (f.type === "支給") return acc + v;
          if (f.type === "控除") return acc - v;
          return acc;
        }, 0);
        const ref = doc(collection(db, "payslips"));
        batch.set(ref, {
          targetEmail: email, companyId: companyId || "",
          yearMonth: month, values, netPay,
          issuedBy: user.email, createdAt: Timestamp.now(),
        });
        count++;
      }
      await batch.commit();
      setResult(`${count}件の給与明細を発行しました`);
      setPreview([]);
      onIssued();
    } catch (err) {
      console.error(err);
      alert("アップロードに失敗しました。もう一度お試しください。");
    } finally {
      setUploading(false);
    }
  };

  if (!template) return <div style={st.form}><p style={{color:"#999"}}>読み込み中...</p></div>;

  return (
    <div style={st.form}>
      <h3 style={{ color: "#2c3e50", marginBottom: "8px" }}>Excelで一括アップロード</h3>
      <p style={{ fontSize: "13px", color: "#7f8c8d", marginBottom: "20px" }}>
        テンプレートタブで「Excelテンプレート」をダウンロードし、データを入力してからここでアップロードしてください。
      </p>

      <label style={{ ...st.label, display: "inline-block" }}>
        <div style={st.uploadArea}>
          <div style={{ fontSize: "32px", marginBottom: "8px" }}>📂</div>
          <div style={{ fontSize: "14px", color: "#2c3e50", fontWeight: "bold" }}>Excelファイルを選択</div>
          <div style={{ fontSize: "12px", color: "#999", marginTop: "4px" }}>.xlsx / .xls</div>
          <input type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={handleFile} />
        </div>
      </label>

      {preview.length > 0 && (
        <>
          <div style={{ marginTop: "20px", marginBottom: "12px", fontWeight: "bold", color: "#2c3e50" }}>
            プレビュー（{preview.length}件）
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ ...st.tbl, marginBottom: "16px" }}>
              <thead>
                <tr>
                  <th style={st.th}>メール</th>
                  <th style={st.th}>対象月</th>
                  {template.fields.map(f => <th key={f.name} style={st.th}>{f.name}</th>)}
                  <th style={{ ...st.th, color: "#27ae60" }}>支給額</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((row, i) => {
                  const net = template.fields.reduce((acc, f) => {
                    const v = Number(row[f.name]) || 0;
                    if (f.type === "支給") return acc + v;
                    if (f.type === "控除") return acc - v;
                    return acc;
                  }, 0);
                  return (
                    <tr key={i}>
                      <td style={st.td}>{row["対象メール"]}</td>
                      <td style={st.td}>{row["対象月(YYYY-MM)"]}</td>
                      {template.fields.map(f => (
                        <td key={f.name} style={{ ...st.td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          {Number(row[f.name] || 0).toLocaleString("ja-JP")}
                        </td>
                      ))}
                      <td style={{ ...st.td, textAlign: "right", color: "#27ae60", fontWeight: "bold" }}>
                        ¥{net.toLocaleString("ja-JP")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button style={st.submitBtn} onClick={handleUpload} disabled={uploading} type="button">
            {uploading ? "アップロード中..." : `${preview.length}件を一括発行`}
          </button>
        </>
      )}

      {result && <div style={st.resultMsg}>{result}</div>}
    </div>
  );
}

// ========== メインコンポーネント ==========
function Payslip({ user, isAdmin, companyId }) {
  const [payslips, setPayslips] = useState([]);
  // 給与計算で確定した明細を最初に見せる。Excel取り込み分は「Excel取込の明細」に分ける。
  const [tab, setTab] = useState("mine");

  useEffect(() => { fetchPayslips(); }, []);

  const fetchPayslips = async () => {
    try {
      let q;
      if (isAdmin && companyId) {
        q = query(collection(db, "payslips"), where("companyId", "==", companyId));
      } else if (isAdmin) {
        q = collection(db, "payslips");
      } else {
        q = query(collection(db, "payslips"), where("targetEmail", "==", user.email));
      }
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.yearMonth || "").localeCompare(a.yearMonth || ""));
      setPayslips(data);
    } catch (e) { console.error(e); }
  };

  const fmtAmt = (n) => `¥${(n || 0).toLocaleString("ja-JP")}`;

  const tabs = isAdmin
    ? [
        { key: "mine",     label: "自分の明細" },
        { key: "issue",    label: "発行" },
        { key: "template", label: "テンプレート編集" },
        { key: "upload",   label: "Excel一括" },
        { key: "list",     label: "Excel取込の明細" },
      ]
    : [
        { key: "mine",     label: "自分の明細" },
        { key: "list",     label: "Excel取込の明細" },
      ];

  return (
    <div style={st.container}>
      <h2 style={st.title}>💰 給与明細</h2>
      <div style={st.tabs}>
        {tabs.map(t => (
          <button key={t.key}
            style={{ ...st.tab, ...(tab === t.key ? st.active : {}) }}
            onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {tab === "mine" && (
        <MyPayslips user={user} companyId={companyId} />
      )}

      {tab === "template" && isAdmin && <TemplateTab companyId={companyId} />}
      {tab === "issue"    && isAdmin && <IssueTab user={user} companyId={companyId} onIssued={() => { fetchPayslips(); setTab("list"); }} />}
      {tab === "upload"   && isAdmin && <UploadTab user={user} companyId={companyId} onIssued={() => { fetchPayslips(); setTab("list"); }} />}

      {tab === "list" && (
        payslips.length === 0
          ? <p style={st.empty}>給与明細はありません</p>
          : payslips.map(p => {
            const fields = p.values
              ? Object.entries(p.values)
              : [
                  ["基本給", p.basicSalary],
                  ["残業代", p.overtime],
                  ["控除額", p.deductions],
                ];
            return (
              <div key={p.id} style={st.card}>
                <div style={st.cardTop}>
                  <span style={st.month}>{p.yearMonth} 分</span>
                  {isAdmin && <span style={st.target}>{p.targetEmail}</span>}
                </div>
                {fields.map(([name, val]) => (
                  <div key={name} style={st.row}>
                    <span>{name}</span>
                    <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtAmt(Number(val))}</span>
                  </div>
                ))}
                <div style={{ ...st.row, borderTop: "2px solid #2c3e50", marginTop: "8px", paddingTop: "8px" }}>
                  <span style={{ fontWeight: "bold" }}>差引支給額</span>
                  <span style={{ fontWeight: "bold", color: "#27ae60", fontSize: "18px" }}>{fmtAmt(p.netPay)}</span>
                </div>
                {p.note && <p style={st.note}>{p.note}</p>}
              </div>
            );
          })
      )}
    </div>
  );
}

const st = {
  container: { padding: "32px", maxWidth: "800px", margin: "0 auto" },
  title: { color: "#2c3e50", marginBottom: "24px" },
  tabs: { display: "flex", gap: "8px", marginBottom: "24px", flexWrap: "wrap" },
  tab: { padding: "10px 20px", border: "2px solid #ddd", borderRadius: "8px", backgroundColor: "#fff", cursor: "pointer", fontSize: "14px", color: "#7f8c8d" },
  active: { border: "2px solid #2c3e50", color: "#2c3e50", fontWeight: "bold" },
  form: { backgroundColor: "#fff", padding: "24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "24px" },
  label: { display: "block", marginBottom: "6px", color: "#2c3e50", fontSize: "14px", fontWeight: "bold" },
  input: { width: "100%", padding: "12px", marginBottom: "16px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "15px", boxSizing: "border-box" },
  submitBtn: { padding: "12px 32px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  dlBtn: { padding: "8px 18px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px" },
  addRowBtn: { marginTop: "12px", padding: "8px 18px", backgroundColor: "#f0f4f8", color: "#2c3e50", border: "1.5px dashed #ccd", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  tbl: { width: "100%", borderCollapse: "collapse", marginBottom: "8px" },
  th: { backgroundColor: "#f0f4f8", color: "#2c3e50", padding: "10px 12px", textAlign: "left", fontSize: "13px", fontWeight: "bold" },
  td: { padding: "8px 12px", borderBottom: "1px solid #eee", fontSize: "14px" },
  tblInput: { width: "100%", padding: "8px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px", boxSizing: "border-box" },
  tblSelect: { padding: "8px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px" },
  delBtn: { background: "none", border: "none", color: "#e74c3c", cursor: "pointer", fontSize: "16px", padding: "4px 8px" },
  previewSection: { fontSize: "12px", fontWeight: "bold", color: "#7f8c8d", letterSpacing: "0.08em", marginBottom: "4px", marginTop: "8px" },
  previewRow: { display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "13px", borderBottom: "1px solid #eee" },
  fieldGroup: { backgroundColor: "#f8fafc", borderRadius: "10px", padding: "16px", marginBottom: "16px" },
  groupLabel: { fontSize: "12px", fontWeight: "bold", color: "#7f8c8d", letterSpacing: "0.08em", marginBottom: "12px" },
  fieldRow: { display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" },
  fieldName: { flex: "0 0 120px", fontSize: "14px", color: "#2c3e50" },
  amtInput: { flex: 1, padding: "10px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "15px", textAlign: "right", fontVariantNumeric: "tabular-nums" },
  yen: { fontSize: "14px", color: "#7f8c8d", flexShrink: 0 },
  netRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px", backgroundColor: "#f0f8f4", borderRadius: "10px", marginBottom: "20px" },
  uploadArea: { border: "2px dashed #ccd", borderRadius: "12px", padding: "32px", textAlign: "center", cursor: "pointer", backgroundColor: "#fafbfc", display: "block" },
  resultMsg: { marginTop: "16px", padding: "12px 16px", backgroundColor: "#d5f5e3", color: "#27ae60", borderRadius: "8px", fontWeight: "bold" },
  empty: { color: "#999", textAlign: "center", marginTop: "40px" },
  card: { backgroundColor: "#fff", padding: "20px 24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "12px" },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" },
  month: { fontSize: "18px", fontWeight: "bold", color: "#2c3e50" },
  target: { fontSize: "13px", color: "#7f8c8d" },
  row: { display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #f0f0f0", fontSize: "15px" },
  note: { marginTop: "12px", fontSize: "13px", color: "#7f8c8d" },
};

export default Payslip;
